import React, { useState, useEffect } from 'react';
import { format } from "date-fns";
import { API_DATE_STRING_FORMAT } from '../../Constants/stringFormats';
import { Button, ErrorAlert, Input } from '../Simple';
import { ApiError } from '../Combined';
import useGenerateEobApi from '../../Hooks/useGenerateEobApi';

const EobComponent = ({ claimId, lines, processedDate, pricerType }) => {
	const [percentOfMedicare, setPercentOfMedicare] = useState(1.5);

	let totalBilled = 0;
	let totalAllowed = 0;
	if (lines && lines.length > 0) {
		lines.map((line) => (
			line["medicareAllowedPreCap"] = Number((line.allowedAmount * percentOfMedicare + Number.EPSILON).toFixed(2))	
		));
		totalBilled = lines.reduce((acc, line) => acc + (line.billedAmount || 0), 0);
		totalAllowed = lines.reduce((acc, line) => acc + (line.medicareAllowedPreCap || 0), 0);

		if (totalAllowed > totalBilled) totalAllowed = totalBilled;
	}
	const shiftAmounts = lines.map(line => line.initialMaxShiftAmount == 0 ? 0 : Math.min(line.initialMaxShiftAmount, totalAllowed < totalBilled ? (line.allowedAmount || 0) * percentOfMedicare : (line.billedAmount || 0)));

	const [claimIdState, setClaimIdState] = useState(claimId);
	const [patientName, setPatientName] = useState('');
	const [memberID, setMemberID] = useState('');
	const [memberPlan, setMemberPlan] = useState('');
	const [prePayValues, setPrePayValues] = useState(shiftAmounts.map(amt => amt.toFixed(2)));
	const [prePayInputValues, setPrePayInputValues] = useState(shiftAmounts.map(amt => amt.toFixed(2)));
	const [patientRespValues, setPatientRespValues] = useState(lines.map((line, i) => ((totalAllowed < totalBilled ?
		line.medicareAllowedPreCap :
		(line.billedAmount || 0)) - shiftAmounts[i]).toFixed(2)));
	const [patientRespInputValues, setPatientRespInputValues] = useState(lines.map((line, i) => ((totalAllowed < totalBilled ?
		line.medicareAllowedPreCap :
		(line.billedAmount || 0)) - shiftAmounts[i]).toFixed(2)));
	const [memberMailRecipient, setMemberMailRecipient] = useState('');
	const [memberMailAddress, setMemberMailAddress] = useState('');
	const [memberMailAddress2, setMemberMailAddress2] = useState('');
	const [providerMailRecipient, setProviderMailRecipient] = useState('');
	const [providerMailAddress, setProviderMailAddress] = useState('');
	const [providerMailAddress2, setProviderMailAddress2] = useState('');
	const [providerName, setProviderName] = useState('');
	const [providerAddress, setProviderAddress] = useState('');
	const [providerAddress2, setProviderAddress2] = useState('');
	const [percentOfMedicareInputValue, setPercentOfMedicareInputValue] = useState((percentOfMedicare * 100).toFixed(0));
	const [genEobLoading, _, genEobError, genEobExecute, genEobReset] = useGenerateEobApi();

	useEffect(() => {
		const timer = setTimeout(() => {
			setPrePayValues(prePayInputValues);
		}, 500);

		return () => clearTimeout(timer);
	}, [prePayInputValues]);

	useEffect(() => {
		const timer = setTimeout(() => {
			setPatientRespValues(patientRespInputValues);
		}, 500);

		return () => clearTimeout(timer);
	}, [patientRespInputValues]);

	useEffect(() => {
		const timer = setTimeout(() => {
			setPercentOfMedicare((parseFloat(percentOfMedicareInputValue) || 0) / 100);
		}, 500);

		return () => clearTimeout(timer);
	}, [percentOfMedicareInputValue]);

	const processedDateString = format(processedDate, API_DATE_STRING_FORMAT);

	const totalDiscount = totalBilled - totalAllowed;

	const totalPrePaid = prePayValues
		.map((value) => parseFloat(value) || 0)
		.reduce((acc, value) => acc + value, 0);

	const totalPatientResp = patientRespValues
		.map((value) => parseFloat(value) || 0)
		.reduce((acc, value) => acc + value, 0);

	const serviceRows = lines && lines.length > 0 ? (
		lines.map((line, index) => (
			<tr key={index}>
				<td>{line.dateOfService}</td>
				<td>{line.serviceCode}</td>
				<td>{line.serviceDescription}</td>
				<td>{line.qty}</td>
				<td><span>$</span>{(line.billedAmount || 0).toFixed(2)}</td>
				<td>
					<span>$</span>
					{(totalAllowed < totalBilled ?
						(line.billedAmount || 0) - (line.medicareAllowedPreCap || 0) :
						0).toFixed(2)}
				</td>
				<td>
					<span>$</span>
					{(totalAllowed < totalBilled ?
						(line.medicareAllowedPreCap || 0) :
						(line.billedAmount || 0)).toFixed(2)}
				</td>
				<td>
					<div className="row align-items-center">
						<div className="col-1" >
							$
						</div>
						<div className="col" style={{ paddingLeft: 0 }}>
							<input
								id={`prePayInputValue{index}`}
								className="form-control"
								type="number"
								value={prePayInputValues[index]}
								placeholder="0.00"
								style={{ paddingLeft: 0 }}
								onChange={(e) => {
									const values = [...prePayValues];
									values[index] = e.target.value;
									setPrePayInputValues(values);
								}}
							/>
						</div>
					</div>
				</td>
				<td>
					<div className="row align-items-center">
						<div className="col-1" >
							$
						</div>
						<div className="col" style={{ paddingLeft: 0 }}>
							<input
								id={`patientRespInputValue{index}`}
								className="form-control"
								type="number"
								value={patientRespInputValues[index]}
								placeholder="0.00"
								style={{ paddingLeft: 0 }}
								onChange={(e) => {
									const values = [...patientRespValues];
									values[index] = e.target.value;
									setPatientRespInputValues(values);
								}}
							/>
						</div>
					</div>
				</td>
			</tr>
		))
	) : (
		<tr>
			<td>&lt;&lt;DOS&gt;&gt;</td>
			<td>&lt;&lt;SC&gt;&gt;</td>
			<td>&lt;&lt;SD&gt;&gt;</td>
			<td>&lt;&lt;QTY&gt;&gt;</td>
			<td>&lt;&lt;BILLEDAMT&gt;&gt;</td>
			<td>&lt;&lt;DISCOUNTAMT&gt;&gt;</td>
			<td>&lt;&lt;ALLOWEDAMT&gt;&gt;</td>
			<td>&lt;&lt;PREPAY&gt;&gt;</td>
			<td>&lt;&lt;PATIENTRES&gt;&gt;</td>
		</tr>
	);

	const generateJSON = () => {
		return {
			pricerType: pricerType,
			memberInfo: {
				memberMailRecipient,
				memberMailAddress,
				memberMailAddress2,
			},
			providerMailingInfo: {
				providerMailRecipient,
				providerMailAddress,
				providerMailAddress2,
			},
			claimInfo: {
				patientName,
				memberID,
				memberPlan,
				claimID: claimIdState,
				processedDate: processedDateString,
				providerName,
				providerAddress,
				providerAddress2
			},
			totals: {
				percentOfMedicare,
				totalBilled,
				totalDiscount,
				totalAllowed,
				totalPrePaid,
				totalPatientResp
			},
			services: lines.map((line, index) => {
				let allowedAmount, discountAmount;
				if (totalAllowed < totalBilled) {
					allowedAmount = (line.medicareAllowedPreCap || 0);
					discountAmount = (line.billedAmount || 0) - allowedAmount;
				} else {
					allowedAmount = line.billedAmount || 0;
					discountAmount = 0;
				}
	
				return {
					serviceDate: line.dateOfService,
					revCode: line.serviceCode,
					serviceDescription: line.serviceDescription,
					adjUnits: line.qty || 0,
					billed: line.billedAmount,
					billedAfterDiscount: discountAmount.toFixed(2),
					allowed: allowedAmount.toFixed(2),
					prePayAmount: parseFloat(prePayInputValues[index]) || 0,
					patientRespAmount: parseFloat(patientRespInputValues[index]) || 0
				};
			}),
		};
	};

	const handleSubmit = async () => {
		genEobReset;
		const nameParts = patientName.split(' ');
		const firstName = nameParts[0];
		const lastName = nameParts[nameParts.length - 1];
		const validDates = lines
			.map(line => line.dateOfService ? new Date(line.dateOfService.split(' - ')[0]) : null)
			.filter(date => date !== null);
		const minDateOfService = validDates.length > 0 ? new Date(Math.min(...validDates)) : null;
		const firstDateOfService = minDateOfService ? format(minDateOfService, 'MMddyyyy') : '';
		const filename = `${firstName}_${lastName}_${firstDateOfService}_${claimId}`;
		await genEobExecute(generateJSON(), filename);
	};

	return (
		<>
			<ApiError error={genEobError} />
			<div className="container mt-5">
				<div className="row mb-4">
					<div className="row d-flex col-md-6">
						<div className="p-3 border bg-light">
							<h4>Member Mailing Information</h4>
							<Input type="text" labelLeft={true} inputWidthClass="col-8" id="memberMailRecipient" name="memberMailRecipient" value={memberMailRecipient} onChange={setMemberMailRecipient} label="Mail Recipient" />
							<p></p>
							<Input type="text" labelLeft={true} inputWidthClass="col-8" id="memberMailAddress" name="memberMailAddress" value={memberMailAddress} onChange={setMemberMailAddress} label="Mail Address" />
							<Input type="text" labelLeft={true} inputWidthClass="col-8" id="memberMailAddress2" name="memberMailAddress2" value={memberMailAddress2} onChange={setMemberMailAddress2} label=" " />
						</div>
						<div className="p-3 border bg-light mt-3">
							<h4>Provider Mailing Information</h4>
							<Input type="text" labelLeft={true} inputWidthClass="col-8" id="providerMailRecipient" name="providerMailRecipient" value={providerMailRecipient} onChange={setProviderMailRecipient} label="Mail Recipient" />
							<p></p>
							<Input type="text" labelLeft={true} inputWidthClass="col-8" id="providerMailAddress" name="providerMailAddress" value={providerMailAddress} onChange={setProviderMailAddress} label="Mail Address" />
							<Input type="text" labelLeft={true} inputWidthClass="col-8" id="providerMailAddress2" name="providerMailAddress2" value={providerMailAddress2} onChange={setProviderMailAddress2} label=" " />
						</div>
					</div>
					<div className="col-md-6">
						<div className="p-3 border bg-light">
							<h4>Claim Information</h4>
							<Input type="text" labelLeft={true} inputWidthClass="col-8" id="claimID" name="claimID" value={claimIdState} onChange={setClaimIdState} label="ClaimID" />
							<p></p>
							<Input type="text" labelLeft={true} inputWidthClass="col-8" id="patientName" name="patientName" value={patientName} onChange={setPatientName} label="Patient Name" />
							<p></p>
							<Input type="text" labelLeft={true} inputWidthClass="col-8" id="memberID" name="memberID" value={memberID} onChange={setMemberID} label="Member ID" />
							<p></p>
							<Input type="text" labelLeft={true} inputWidthClass="col-8" id="memberPlan" name="memberPlan" value={memberPlan} onChange={setMemberPlan} label="Plan Level" />
							<p></p>
							<Input type="text" labelLeft={true} inputWidthClass="col-8" id="processedDate" name="processedDate" value={processedDateString} label="Processed Date" disabled={true} />
							<p></p>
							<Input type="text" labelLeft={true} inputWidthClass="col-8" id="providerName" name="providerName" value={providerName} onChange={setProviderName} label="Provider Name" />
							<p></p>
							<Input type="text" labelLeft={true} inputWidthClass="col-8" id="providerAddress" name="providerAddress" value={providerAddress} onChange={setProviderAddress} label="Provider Address" />
							<Input type="text" labelLeft={true} inputWidthClass="col-8" id="providerAddress2" name="providerAddress2" value={providerAddress2} onChange={setProviderAddress2} label=" " />
							<p></p>
							<br />

						</div>
					</div>
				</div>
			</div>
			<div className="row mb-4">
				<div className="col-12">
					<table className="table table-bordered">
						<thead className="thead-light">
							<tr>
								<th>Totals:</th>
								<th>Percent of Medicare</th>
								<th>Total Billed</th>
								<th>Total Discount</th>
								<th>Total Allowed</th>
								<th>Total Prepaid</th>
								<th>Patient Responsibility Total</th>
							</tr>
						</thead>
						<tbody>
							<tr>
								<td></td>
								<td>
									<div className="row align-items-center">
										<div className="col-3" style={{ paddingRight: 0 }}>
											<input
												type="decimal"
												id="percentOfMedicare"
												className="form-control"
												name="percentOfMedicare"
												style={{ paddingRight: 0 }}
												value={percentOfMedicareInputValue}
												onChange={(e) => setPercentOfMedicareInputValue(e.target.value)}
											/>
										</div>
										<div className="col" style={{ paddingLeft: 2 }}>
											%
										</div>
									</div>
								</td>
								<td><span>$</span>{totalBilled.toFixed(2)}</td>
								<td><span>$</span>{totalDiscount.toFixed(2)}</td>
								<td><span>$</span>{totalAllowed.toFixed(2)}</td>
								<td><span>$</span>{totalPrePaid.toFixed(2)}</td>
								<td><span>$</span>{totalPatientResp.toFixed(2)}</td>
							</tr>
						</tbody>
					</table>
				</div>
			</div>

			<div className="row">
				<div className="col-12">
					<table className="table table-bordered">
						<thead className="thead-light">
							<tr>
								<th>Date of Service</th>
								<th>Service Code</th>
								<th>Service Description</th>
								<th>QTY</th>
								<th>Billed Amount</th>
								<th>Discount Amount</th>
								<th>Allowed Amount</th>
								<th>Pre-Pay Amount</th>
								<th>Patient Responsibility</th>
							</tr>
						</thead>
						<tbody>
							{serviceRows}
						</tbody>
					</table>
				</div>
			</div>
			<div className="row justify-content-between my-2 sticky bottom g-0 border-top">
				<div className="col-auto align-self-center">
					<ErrorAlert visible={genEobError} />
				</div>
				<div className="col-auto align-self-center">
					<div className="row g-0 justify-cotent-end">
						<div className="col-auto ms-2">
							<Button className="btn btn-primary" id="submit"
								onClick={handleSubmit}
								disabled={genEobLoading}
								text="Submit"
								loading={genEobLoading} />
						</div>
					</div>
				</div>
			</div>
		</>
	);
};

export default EobComponent;
